import { ReactNode, useEffect, useMemo, useState } from "react";
import { HeadCell, DataRow } from "@/components/DataTable/dataTable.interface";
import { useGetTestResults } from "@/hooks/useEnrollments";
import { Box, Tooltip } from "@mui/material";
import { EnrollmentStatus, TestResult } from "@/domain/enrollment.interface";
import ActionMenu from "@/components/ActionMenu";
import { createDataRow } from "@/components/DataTable/createDataRow";
import DataTable from "@/components/DataTable";
import ChipStatus from "@/components/ChipStatus";
import { TriangleAlert } from "lucide-react";
import { MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";
import { ACTION_CODE } from "@/domain/auditlog.enum";
import { useAuditLogFirstRender } from "@/hooks/useAuditLogs";
import { useUserStore } from "@/hooks/useUserStore";
import { formatCurrency, getLocaleByCountry } from "@/utils/formatters";
import { useCountrySetting, useCurrencySetting } from "@/hooks/useSetting";
import { TestResultStatusEnum } from "@/domain/enrollment.enum";
import {
  formatInBackofficeLondon,
} from "@/utils/dateUtils";

type TestResultsTabProps = {
  enrollmentId: string;
  handleViewImages: (
    testResultId: string,
    enrollmentId: string,
    enrollmentStatus: EnrollmentStatus,
    validationType: string,
  ) => void;
  onRefetchTestResults: (refetchFn: () => void) => void;
  programName: string;
  participantName: string;
  enrollmentStatus: EnrollmentStatus;
  shouldLoadData?: boolean; // AC02: Only load data when section is expanded
};

const TestResultsTab: React.FC<TestResultsTabProps> = ({
  enrollmentId,
  handleViewImages,
  onRefetchTestResults,
  programName,
  participantName,
  enrollmentStatus,
  shouldLoadData = true, // AC02: Default to true for backward compatibility
}) => {
  const { userInfo } = useUserStore();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5); // AC05: Default to 5 rows per page
  const [sortField, setSortField] = useState<string>("validationDate"); // Default sort field
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc"); // Default sort order

  const countrySetting = useCountrySetting();
  const currency = useCurrencySetting();
  const country = useMemo(() => countrySetting, [countrySetting]);
  const locale = useMemo(() => getLocaleByCountry(country), [country]);

  // AC02: Only fetch data if shouldLoadData is true
  const {
    testResults,
    isLoadingTestResults,
    refetchTestResults: refetchTestResultsFromHook,
  } = useGetTestResults({
    enrollmentId,
    pageNumber: page + 1,
    pageSize: rowsPerPage,
    sortField,
    sortOrder,
    enabled: shouldLoadData, // AC02: Only fetch when section is expanded
  });

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangeSort = (order: string, orderBy: keyof DataRow) => {
    setSortField(orderBy as string);
    setSortOrder(order === "asc" ? "asc" : "desc");
    setPage(0); // Reset to first page when sorting changes
  };

  useEffect(() => {
    onRefetchTestResults(refetchTestResultsFromHook);
  }, [refetchTestResultsFromHook, onRefetchTestResults]);

  // AC02: Only refetch when shouldLoadData is true
  useEffect(() => {
    if (shouldLoadData) {
      refetchTestResultsFromHook();
    }
  }, [
    page,
    rowsPerPage,
    sortField,
    sortOrder,
    refetchTestResultsFromHook,
    shouldLoadData,
  ]);

  useEffect(() => {
    if (shouldLoadData) {
      refetchTestResultsFromHook();
    }
  }, [enrollmentStatus, shouldLoadData, refetchTestResultsFromHook]);

  const rows = useMemo(() => {
    if (!testResults || (testResults && testResults.testResults.length === 0))
      return [];

    return (
      testResults?.testResults.map((testResult: TestResult) => {
        // COYN-1148: Pending status should display dash and disable View Images
        const isPending = testResult.validationStatus === "PENDING";
        // AC00-B: Only show "View images" option if user has permission
        // COYN-1078: Disable "View images" for Non-Carriage validation type
        const isNonCarriage =
          testResult.validationStatus?.toUpperCase() === "NON_CARRIAGE" ||
          testResult.validationStatus?.toUpperCase() === "NON-CARRIAGE";
        const actionMenu = [
          {
            value: "View images",
            onClick: () =>
              handleViewImages(
                testResult._id,
                enrollmentId,
                enrollmentStatus,
                testResult.validationType,
              ),
            disabled:
              testResult.validationStatus === TestResultStatusEnum.MISSED ||
              isNonCarriage ||
              isPending,
          },
        ];

        // Format validation date as "DD MMM YYYY, hh:mm A" (e.g., "04 Oct 2025, 01:16 PM")
        // completedAt: ISO → London. Fallback `day` is built server-side in account TZ → parse in that TZ, display London.
        let validationDate = "-";
        if (testResult.completedAt) {
          const formatted = formatInBackofficeLondon(
            testResult.completedAt,
            "DD MMM YYYY, hh:mm A",
          );
          validationDate = formatted || "-";
        } else if (testResult.day) {
          // `day` is already produced by backend using `$dateToString` with timezone applied.
          // Don't parse/reformat here to avoid any accidental "double conversion".
          validationDate = testResult.day;
        }

        // COYN-1068: Get validation attempts - use validationAttempts (proofParticipant length) if available, otherwise fallback to validationNumber
        // COYN-1148: Show dash (-) for Pending status
        const validationAttempts = isPending
          ? "-"
          : testResult.validationAttempts !== undefined
            ? testResult.validationAttempts.toString()
            : testResult.validationNumber || "-";

        const record: Record<string, ReactNode> = {
          validationType: testResult.validationType,
          validationStatus:
            testResult.validationStatus === "PENDING" ? (
              "-"
            ) : (
              <ChipStatus
                status={testResult.validationStatus}
                validationType={testResult.validationType}
              />
            ),
          validationDate: validationDate,
          validationAttempts: validationAttempts,
          rewardsEarned: formatCurrency(
            // COYN-1068: Backend now returns "0" instead of "-" when reward is 0
            testResult.rewardEarned === "-" || !testResult.rewardEarned
              ? 0
              : parseFloat(testResult.rewardEarned) || 0,
            currency.currency,
            locale,
          ),
          actions:
            !testResult.canValidate &&
            testResult.validationStatus === "PENDING" &&
            enrollmentStatus === EnrollmentStatus.ACTIVE ? (
              <div className="flex items-center justify-end p-2">
                <Tooltip title="There are previous pending validation requests that you must review before this one.">
                  <TriangleAlert fontSize="small" className="text-red-500" />
                </Tooltip>
              </div>
            ) : (
              <ActionMenu
                id={testResult._id}
                options={actionMenu}
                disabled={testResult.validationType === "On demand Incentive"}
              />
            ),
        };

        const row = createDataRow(testResult._id, record);

        return row;
      }) || []
    );
  }, [testResults, enrollmentStatus]);

  // AC05: Update headCells according to Fields Spec
  const headCells = useMemo(() => {
    const baseHeadCells: HeadCell[] = [
      {
        id: "validationType",
        isSortable: false,
        disablePadding: true,
        label: "Validation type",
        align: "left",
        alwaysVisible: true,
      },
      {
        id: "validationStatus",
        isSortable: false,
        disablePadding: true,
        label: "Validation status",
        align: "center",
        alwaysVisible: true,
      },
      {
        id: "validationDate",
        isSortable: true,
        disablePadding: true,
        label: "Validation date",
        align: "center",
      },
      {
        id: "validationAttempts",
        isSortable: false,
        disablePadding: true,
        label: "Validation attempts",
        align: "center",
        hideOnMobile: true,
      },
      {
        id: "rewardsEarned",
        isSortable: false,
        disablePadding: true,
        label: "Rewards earned",
        align: "center",
      },
      {
        id: "actions",
        isSortable: false,
        disablePadding: false,
        label: "Actions",
        align: "right",
        width: "100px",
        alwaysVisible: true,
      },
    ];

    return baseHeadCells;
  }, []);

  // AC00-A: Logging when user accesses the results view
  useAuditLogFirstRender({
    appType: "WEB_BACK_OFFICE",
    module: MODULE_NAME.ENROLLMENTS,
    option: OPTIONS.ENROLLMENT_DETAIL_TEST,
    actionCode: ACTION_CODE.WEB_PART_ENROLL_RESULTS_VIEWED,
    action: "When viewing participant enrollment results",
    detail: `Viewed enrollment results of ${programName} for ${participantName}`,
    transactionDate: new Date(),
    accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
    createdBy: userInfo?._id as string,
    name: `${userInfo?.firstName} ${userInfo?.lastName}`,
  });

  // Keep existing audit log for backward compatibility
  useAuditLogFirstRender({
    appType: "WEB_BACK_OFFICE",
    module: MODULE_NAME.ENROLLMENTS,
    option: OPTIONS.ENROLLMENT_DETAIL_TEST,
    actionCode: ACTION_CODE.WEB_ENR_PART_DETAIL_TEST,
    action: "When viewing an enrollment detail - Test results",
    detail: `Viewed enrollment detail - test results of ${programName} for ${participantName}`,
    transactionDate: new Date(),
    accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
    createdBy: userInfo?._id as string,
    name: `${userInfo?.firstName} ${userInfo?.lastName}`,
  });

  // AC03: Show message when no results
  if (!shouldLoadData) {
    return null; // AC02: Don't render anything if data shouldn't be loaded
  }

  return (
    <Box sx={{ width: "100%", minWidth: 0, overflow: "hidden" }}>
      <DataTable
        data={rows}
        headCells={headCells}
        rowsPerPage={rowsPerPage}
        page={page}
        total={testResults?.totalCount || 0}
        isLoading={isLoadingTestResults}
        order={sortOrder}
        orderBy={sortField as keyof DataRow} // AC05: Sort by Validation date desc by default
        onChangePage={handleChangePage}
        onChangeSize={handleChangeRowsPerPage}
        messageEmptyData="No records to show." // AC03: Message when no results
        bodyCellFontSize="14px"
        onChangeSort={handleChangeSort}
        minWidth={700}
      />
    </Box>
  );
};

export default TestResultsTab;
